import * as React from "react";
import { useForm, useFormState } from 'react-final-form';
import { useDataProvider, useNotify } from 'react-admin';
import Button from '@material-ui/core/Button';
import AutorenewIcon from '@material-ui/icons/Autorenew';

const BASE_PLACES = [
    { key: "full_name", source: "field", field: "employment.name" },
    { key: "job_title", source: "field", field: "employment.job_title" },
    { key: "department", source: "field", field: "employment.department.name" },
    { key: "email", source: "field", field: "employment.email", font_size: 9 },
    { key: "phone_no", source: "field", field: "employment.phone" },
    { key: "agent_name", source: "field", field: "employment.agent_name" },
    { key: "agent_email", source: "field", field: "employment.agent_email" },
    { key: "tax_status", source: "eav", slug: "employment_type" },

    { key: "prod_wk", source: "choice", field: "employment.prod_meeting", equals: "work" },
    { key: "prod_hol", source: "choice", field: "employment.prod_meeting", equals: "designated_holiday" },
    { key: "rt_work", source: "choice", field: "employment.read_through", equals: "work" },
    { key: "rt_hol", source: "choice", field: "employment.read_through", equals: "designated_holiday" },

    { key: "pre_prep_start", source: "field", field: "employment.pre_prep_start_date" },
    { key: "pre_prep_end", source: "field", field: "employment.pre_prep_end_date" },
    { key: "pre_prep_days", source: "field", field: "employment.pre_prep_days" },
    { key: "pre_prep_weeks", source: "field", field: "employment.pre_prep_weeks" },

    { key: "prep_start", source: "field", field: "employment.prep_start_date" },
    { key: "prep_end", source: "field", field: "employment.prep_end_date" },
    { key: "prep_days", source: "field", field: "employment.prep_days" },
    { key: "prep_weeks", source: "field", field: "employment.prep_weeks" },

    { key: "shoot_start", source: "field", field: "employment.shoot_start_date" },
    { key: "shoot_end", source: "field", field: "employment.shoot_end_date" },
    { key: "shoot_days", source: "field", field: "employment.shoot_days" },
    { key: "shoot_weeks", source: "field", field: "employment.shoot_weeks" },

    { key: "wrap_start", source: "field", field: "employment.wrap_start_date" },
    { key: "wrap_end", source: "field", field: "employment.wrap_end_date" },
    { key: "wrap_days", source: "field", field: "employment.wrap_days" },
    { key: "wrap_weeks", source: "field", field: "employment.wrap_weeks" },

    { key: "total_days", source: "field", field: "employment.total_days" },
    { key: "total_weeks", source: "field", field: "employment.total_weeks" },

    { key: "daily_rate_gross", source: "field", field: "employment.daily_rate_gross" },
    { key: "daily_rate_net", source: "field", field: "employment.daily_rate_net" },
    { key: "daily_rate_hol", source: "field", field: "employment.daily_rate_holiday" },

    { key: "ach_gross", source: "field", field: "employment.hourly_rate_gross" },
    { key: "ach_net", source: "field", field: "employment.hourly_rate_net" },
    { key: "ach_hol", source: "field", field: "employment.hourly_rate_hol" },

    { key: "shoot_rate_gross", source: "computed", resolver: "shoot_period_total", variant: "gross" },
    { key: "shoot_rate_net", source: "computed", resolver: "shoot_period_total", variant: "net" },
    { key: "shoot_rate_hol", source: "computed", resolver: "shoot_period_total", variant: "holiday" },

    { key: "crew_sign", source: "signer", recipient: "crew", type: "signature" },
    { key: "crew_sign_date", source: "signer_date", recipient: "crew" },
    { key: "prod_sign", source: "signer", recipient: "production", type: "signature" },
    { key: "prod_sign_date", source: "signer_date", recipient: "production" },
];

export const GenerateMappingButton = () => {
    const form = useForm();
    const { values } = useFormState();
    const dataProvider = useDataProvider();
    const notify = useNotify();

    const handleGenerate = async () => {
        const productionId = values.production_id;

        if (!productionId) {
            notify('No production set on this template yet', 'warning');
            return;
        }

        try {
            const { data: penaltyTypes } = await dataProvider.getList('production-penalty-types', {
                filter: { production_id: productionId },
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'id', order: 'ASC' },
            });

            const penaltyPlaces = penaltyTypes.flatMap(pt => ([
                { key: `ot_${pt.code}_gross`, source: "penalty_rate", penalty_type_code: pt.code, variant: "gross" },
                { key: `ot_${pt.code}_net`, source: "penalty_rate", penalty_type_code: pt.code, variant: "net" },
                { key: `ot_${pt.code}_hol`, source: "penalty_rate", penalty_type_code: pt.code, variant: "holiday" },
            ]));

            const generated = { places: [...BASE_PLACES, ...penaltyPlaces] };

            form.change('place_mapping', generated);
            notify('Mapping generated — review before saving', 'info');
        } catch (e) {
            notify('Failed to fetch penalty types for this production', 'warning');
        }
    };

    return (
        <Button onClick={handleGenerate} startIcon={<AutorenewIcon />}>
            Generate Starter Mapping
        </Button>
    );
};
